import React, { useState, useEffect, useRef } from 'react';
import { Flex, Box, Tooltip } from '@chakra-ui/react';
import { KTag, KText } from '@/ui-library/components';
import { useIsMobile } from '@/hooks/use-IsMobile';
import { MOBILE_BREAKPOINT } from '@/constants/mobile-breakpoints';
import { useRequisitionGroupSiblings } from '@/components/shared/requisitions/hooks/useRequisitionGroupSiblings';

const GROUP_TOOLTIP_TITLE = 'Requisitions in this group';
const GROUP_LOADING_TEXT = 'Loading requisitions...';
const GROUP_EMPTY_TEXT = 'No requisitions found in this group';

const getGroupColor = (
  groupSize: number
): { bgColor: string; color: string } => {
  const colorIndex = Math.abs(groupSize) % 6;

  const colorMap = [
    { bgColor: '#FAF0FA', color: '#960D96' },
    { bgColor: '#E5F4F4', color: '#0A5B5B' },
    { bgColor: 'pastel-3-light', color: 'pastel-3-dark' },
    { bgColor: 'pastel-4-light', color: 'pastel-4-dark' },
    { bgColor: 'pastel-5-light', color: 'pastel-5-dark' },
    { bgColor: 'pastel-6-light', color: 'pastel-6-dark' },
  ];

  return colorMap[colorIndex];
};

/**
 * Helper function to extract sequence number from requisition code
 * e.g., "VV-214-3" -> 3
 */
const getSequenceNumber = (code: string): number => {
  const parts = code.split('-');
  return parseInt(parts[parts.length - 1], 10) || 0;
};

// To display group requisition codes as tags
interface GroupRequisitionCodesProps {
  requisitions?: any[];
  codes?: string[];
  gap?: string;
  codeStyles?: any;
}

export const GroupRequisitionCodes: React.FC<GroupRequisitionCodesProps> = ({
  requisitions,
  codes,
  gap = '6px',
  codeStyles = {},
}) => {
  const groupHoverStyles = {
    bgColor: 'white',
    borderColor: 'border-color',
    color: 'secondary-700',
    borderWidth: '1px',
  };

  // Handle requisition objects
  if (requisitions && requisitions.length > 0) {
    const sortedRequisitions = [...requisitions].sort((a, b) => {
      return getSequenceNumber(a.code) - getSequenceNumber(b.code);
    });

    return (
      <Flex flexWrap="wrap" gap={gap} {...codeStyles}>
        {sortedRequisitions.map((req) => (
          <KTag
            key={req.id}
            value={req.code}
            color="secondary-700"
            _groupHover={groupHoverStyles}
          />
        ))}
      </Flex>
    );
  }

  // Handle code strings
  if (codes && codes.length > 0) {
    const sortedCodes = [...codes].sort((a, b) => {
      return getSequenceNumber(a) - getSequenceNumber(b);
    });

    return (
      <Flex flexWrap="wrap" gap={gap} {...codeStyles}>
        {sortedCodes.map((code) => (
          <KTag
            key={code}
            value={code}
            color="secondary-700"
            _groupHover={groupHoverStyles}
          />
        ))}
      </Flex>
    );
  }

  return null;
};

interface PartOfGroupTagProps {
  requisition: any;
  /**
   * Pre-resolved group members, for callers that already hold them — the
   * approvals surfaces get the whole group in their own payload. Omit it and
   * the group is fetched from `/requisitions/group` the first time the tooltip
   * opens, which is what the requisitions list does.
   */
  allRequisitions?: any[];
}

export const PartOfGroupTag: React.FC<PartOfGroupTagProps> = ({
  requisition,
  allRequisitions,
}) => {
  const isMobile = useIsMobile({ mobileBreakpoint: MOBILE_BREAKPOINT });
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMobile) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing; not refactoring during a mechanical import move
      setIsTooltipOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setIsTooltipOpen(false);
      }
    };

    if (isTooltipOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTooltipOpen]);

  const {
    requisition_group_id,
    group_size,
    id: currentReqId,
    group,
  } = requisition as any;

  // Get the group ID from either field
  const groupId = requisition_group_id || group?.id;
  const isPartOfGroup = !!groupId;

  // The codes are only ever read inside the tooltip, so they are fetched the
  // first time it opens rather than on mount. Latched, not tracked: once the
  // group has been loaded it stays cached for repeat hovers.
  const [hasOpened, setHasOpened] = useState(false);
  const hasProvidedGroup = Boolean(allRequisitions?.length);
  const { siblings, isLoading: isFetchingGroup } = useRequisitionGroupSiblings({
    groupId,
    enabled: hasOpened && !hasProvidedGroup,
  });

  const groupSource = hasProvidedGroup ? allRequisitions! : siblings;
  const isLoading = hasProvidedGroup ? false : isFetchingGroup;

  const allGroupRequisitions = React.useMemo(() => {
    if (!isPartOfGroup || groupSource.length === 0) {
      return [];
    }

    const groupReqs = groupSource.filter((req: any) => {
      const reqGroupId = req.requisition_group_id || req.group?.id;
      return reqGroupId && reqGroupId === groupId;
    });

    // Add current requisition if not already in the list
    const currentReqInList = groupReqs.some(
      (req: any) => req.id === currentReqId
    );
    if (!currentReqInList) {
      groupReqs.push(requisition);
    }

    return groupReqs;
  }, [isPartOfGroup, groupSource, groupId, currentReqId, requisition]);

  // Get consistent color for this group based on group_size
  const groupColor = React.useMemo(() => {
    if (!isPartOfGroup || !group_size) return null;
    return getGroupColor(group_size);
  }, [isPartOfGroup, group_size]);

  // Always non-empty for a grouped requisition: an undefined label makes Chakra
  // skip the tooltip entirely, and then `onOpen` never fires to trigger the fetch.
  const tooltipLabel = isPartOfGroup ? (
    <Flex flexDir="column" gap="8px" p="4px">
      {allGroupRequisitions.length > 0 ? (
        <>
          <KText>{GROUP_TOOLTIP_TITLE}</KText>
          <GroupRequisitionCodes requisitions={allGroupRequisitions} />
        </>
      ) : (
        <KText>
          {!hasProvidedGroup && (isLoading || !hasOpened)
            ? GROUP_LOADING_TEXT
            : GROUP_EMPTY_TEXT}
        </KText>
      )}
    </Flex>
  ) : undefined;

  if (!isPartOfGroup || !groupColor) {
    return null;
  }

  // Mobile view with click functionality
  if (isMobile) {
    return (
      <Box position="relative">
        <KTag
          value="Part of a group"
          cursor="pointer"
          color={groupColor.color}
          bgColor={groupColor.bgColor}
          width="max-content"
          size="xs-regular"
          whiteSpace="nowrap"
          onClick={() => {
            setHasOpened(true);
            setIsTooltipOpen(!isTooltipOpen);
          }}
          _groupHover={{
            bgColor: 'white',
            borderColor: 'border-color',
            color: 'secondary-700',
            borderWidth: '1px',
          }}
        />
        {isTooltipOpen && allGroupRequisitions.length > 0 && (
          <Box
            ref={tooltipRef}
            position="absolute"
            zIndex={9999}
            bg="white"
            color="secondary-700"
            p="12px"
            borderRadius="6px"
            minW="120px"
            maxW="280px"
            width="fit-content"
            border="1px solid"
            borderColor="border-color"
            boxShadow="drop-shadows-bottom"
            mt="4px"
            right="0"
            left="0"
          >
            <Flex flexDir="column" gap="8px" p="4px">
              <KText size="sm-medium">{GROUP_TOOLTIP_TITLE}</KText>
              <GroupRequisitionCodes
                requisitions={allGroupRequisitions}
                gap="4px"
              />
            </Flex>
          </Box>
        )}
        {isTooltipOpen && allGroupRequisitions.length === 0 && (
          <Box
            ref={tooltipRef}
            position="absolute"
            zIndex={9999}
            bg="white"
            color="secondary-700"
            p="12px"
            borderRadius="6px"
            minW="120px"
            maxW="280px"
            width="fit-content"
            border="1px solid"
            borderColor="border-color"
            boxShadow="drop-shadows-bottom"
            mt="4px"
            right="0"
            left="0"
          >
            <KText size="sm-medium">
              {isLoading ? GROUP_LOADING_TEXT : GROUP_EMPTY_TEXT}
            </KText>
          </Box>
        )}
      </Box>
    );
  }

  // Desktop view with tooltip functionality
  return (
    <Tooltip
      label={tooltipLabel}
      onOpen={() => setHasOpened(true)}
      placement="bottom-start"
      bg="white"
      color="secondary-700"
      p="12px"
      borderRadius="6px"
      // maxW={isMobile ? 'calc(100vw - 32px)' : '400px'}
      maxW="400px"
      border="1px solid"
      borderColor="border-color"
      boxShadow="drop-shadows-bottom"
      isDisabled={isMobile}
    >
      <Box>
        <KTag
          value="Part of a group"
          cursor="pointer"
          color={groupColor.color}
          bgColor={groupColor.bgColor}
          width="max-content"
          size="xs-regular"
          _groupHover={{
            bgColor: 'white',
            borderColor: 'border-color',
            color: 'secondary-700',
            borderWidth: '1px',
          }}
        />
      </Box>
    </Tooltip>
  );
};
